import {
  SLIDE_LEFT_ANIMATION,
  SLIDE_RIGHT_ANIMATION,
  SLIDE_UP_ANIMATION,
} from "fixtures/motionAnimations";
import { AnimatePresence, motion, MotionProps } from "framer-motion";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { Post } from "store/posts/postConstants";
import PostEditorModal from "../PostEditor/PostEditor/PostEditorModal";
import { LoadingType } from "../utils/useUpdatePostActions";

export type PostCardCommonProps = {
  openPostEditor: (instant?: boolean) => void;
};

/** Wrap a Post Card component with state to control the PostEditor modal, and animations. */
const PostCardWrapper = ({
  post,
  loadingPostAction,
  children,
}: {
  post: Post;
  loadingPostAction: LoadingType | false;
  children: FunctionComponent<PostCardCommonProps>;
}) => {
  const [showEditor, setShowEditor] = useState<boolean>(false);
  const [hidePost, setHidePost] = useState(false);

  useEffect(() => {
    if (
      (loadingPostAction &&
        ["save", "schedule", "delete", "publish"].includes(
          loadingPostAction
        )) ||
      (loadingPostAction === "update-review-status" && post.status === "NEW")
    ) {
      setHidePost(true);
    }
  }, [loadingPostAction, post.status]);

  let postEditorTimeout: ReturnType<typeof setTimeout>;
  const openPostEditor = (isInstant?: boolean) => {
    const openModal = () => {
      if (post.status !== "PUBLISHED") {
        setShowEditor(true);
      }
    };

    if (isInstant) {
      openModal();
    } else {
      clearTimeout(postEditorTimeout);
      postEditorTimeout = setTimeout(openModal, 300);
    }
  };

  const useAnimation = useMemo<MotionProps>(() => {
    if (
      !loadingPostAction ||
      (loadingPostAction === "update-review-status" && post.status === "LIKED")
    ) {
      return SLIDE_UP_ANIMATION;
    } else if (
      loadingPostAction === "delete" ||
      (post.status === "SCHEDULED" && loadingPostAction === "save")
    ) {
      return SLIDE_LEFT_ANIMATION;
    } else {
      return SLIDE_RIGHT_ANIMATION;
    }
  }, [post.status, loadingPostAction]);

  return (
    <motion.div layout key={post.id}>
      <AnimatePresence mode="wait">
        {!hidePost && (
          <motion.div key={post.id + "-internal-content"} {...useAnimation}>
            {children({ openPostEditor })}
          </motion.div>
        )}
      </AnimatePresence>

      <PostEditorModal
        existingPost={post}
        isOpen={showEditor}
        closeEditor={() => setShowEditor(false)}
      />
    </motion.div>
  );
};

export default PostCardWrapper;
